import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AleaInput from '../../components/AleaInput/AleaInput';
import AleaDropdown from '../../components/AleaDropdown/AleaDropdown';
import AleaTelephone from '../../components/AleaTelephone/AleaTelephone';
import AccountService from '../../services/AccountService';
import { useAuth } from '../../context/AuthContext';
import AleaCheckIcon from '../../icons/AleaCheckIcon.tsx';
import { positionOptions } from '../../constants/positionOptions.ts';
import { titleOptions } from '../../constants/titleOptions.ts';
import AuthService from '../../services/AuthService.ts';

interface AccountSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AccountSetupFormValues {
  title?: string;
  firstName: string;
  lastName: string;
  position: string;
  compound_phone_number: { code: string; phone: string };
  compound_mobile_phone_number?: {
    code?: string | null;
    phone?: string | null;
  } | null;
}

const AccountSetupModal: React.FC<AccountSetupModalProps> = ({ isOpen }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { user, logout, setShowAccountSetupModal, setUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = yup.object().shape({
    // title: yup.string().required(t('requiredField', { field: t('title') })),
    title: yup.string().optional(),
    firstName: yup
      .string()
      .required(t('requiredField', { field: t('firstName') })),
    lastName: yup
      .string()
      .required(t('requiredField', { field: t('lastName') })),
    position: yup
      .string()
      .required(t('requiredField', { field: t('position') })),
    compound_phone_number: yup
      .object({
        code: yup
          .string()
          .required(t('requiredField', { field: t('general.countryCode') })),
        phone: yup
          .string()
          .required(t('requiredField', { field: t('telLandline') })),
      })
      .required(t('requiredField', { field: t('telLandline') })),
    compound_mobile_phone_number: yup
      .object({
        code: yup.string().nullable(),
        phone: yup.string().nullable(),
      })
      .nullable()
      .notRequired(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<AccountSetupFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      title: '',
      firstName: '',
      lastName: '',
      position: '',
      compound_phone_number: { code: '', phone: '' },
      compound_mobile_phone_number: { code: '', phone: '' },
    },
  });

  const accountTitle = watch('title');

  useEffect(() => {
    if (isOpen && user) {
      const currentPosition =
        user.position && positionOptions.includes(user.position)
          ? user.position
          : positionOptions[0];

      const currentTitle =
        user.title && titleOptions.includes(user.title)
          ? user.title
          : titleOptions[0];

      reset({
        title: currentTitle,
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        position: currentPosition,
        compound_phone_number: user.compound_phone_number || {
          code: '',
          phone: '',
        },
        compound_mobile_phone_number: user.compound_mobile_phone_number || {
          code: '',
          phone: '',
        },
      });
    }
  }, [isOpen, user, reset]);

  const onSubmit: SubmitHandler<AccountSetupFormValues> = async (data) => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      // Handle mobile phone number
      const mobile = data.compound_mobile_phone_number;
      const payloadMobile: { code: string; phone: string } | null =
        mobile &&
        typeof mobile.code === 'string' &&
        mobile.code.trim() !== '' &&
        typeof mobile.phone === 'string' &&
        mobile.phone.trim() !== ''
          ? { code: mobile.code, phone: mobile.phone }
          : null;

      await AccountService.updateAccount(user.id, {
        title: data.title,
        first_name: data.firstName,
        last_name: data.lastName,
        position: data.position,
        compound_phone_number: data.compound_phone_number,
        compound_mobile_phone_number: payloadMobile,
        username: user.username,
        role: user.role,
      });

      // Fetch updated user data
      const updatedUser = await AuthService.me();
      setUser(updatedUser);

      setShowAccountSetupModal(false);

      toast({
        title: t('success'),
        description: t('general.changesSaved'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t('error'),
        description: t('accountSetupFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}} // Non-closable modal
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered
      size="xl"
    >
      <ModalOverlay zIndex={1500} />
      <ModalContent
        zIndex={1501}
        maxWidth="650px"
        p="50px"
        bg="#F3F2F2"
        borderRadius="20px"
      >
        <ModalHeader p="0" mb={12}>
          <Text
            fontSize="25px"
            fontStyle="normal"
            fontWeight="500"
            lineHeight="normal"
          >
            Vervollständigen Sie Ihr Profil
          </Text>
          <Text
            color="gray.600"
            fontSize="15px"
            fontStyle="normal"
            fontWeight="300"
            lineHeight="normal"
          >
            Die Angaben sind sichtbar für andere Cockpit-Nutzer in Ihrer
            Einrichtung.
          </Text>
        </ModalHeader>
        <ModalBody p="0">
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={5}>
              {/* Title Dropdown */}
              <Box width="176px" alignSelf="flex-start">
                <AleaDropdown
                  label={t('title')}
                  options={titleOptions.map((option) => t(option))}
                  value={accountTitle ? t(accountTitle) : t(titleOptions[0])}
                  onChange={(translatedValue) => {
                    // Translated value'dan orijinal key'i bul
                    const originalKey = titleOptions.find(
                      (option) => t(option) === translatedValue
                    );
                    setValue('title', originalKey || titleOptions[0]);
                  }}
                />
              </Box>

              {/* First Name and Last Name */}
              <Flex width="100%" gap={5}>
                <Box flex="1">
                  <AleaInput
                    label={t('firstName')}
                    value={watch('firstName') || ''}
                    onChange={(e) => setValue('firstName', e.target.value)}
                    register={register('firstName')}
                    error={errors.firstName?.message}
                  />
                </Box>
                <Box flex="1">
                  <AleaInput
                    label={t('lastName')}
                    value={watch('lastName') || ''}
                    onChange={(e) => setValue('lastName', e.target.value)}
                    register={register('lastName')}
                    error={errors.lastName?.message}
                  />
                </Box>
              </Flex>

              {/* Position Dropdown */}
              <Box width="100%">
                <AleaDropdown
                  label={t('position')}
                  options={positionOptions.map((option) => t(option))}
                  value={
                    watch('position')
                      ? t(watch('position'))
                      : t(positionOptions[0])
                  }
                  onChange={(translatedValue) => {
                    // Translated value'dan orijinal key'i bul
                    const originalKey = positionOptions.find(
                      (option) => t(option) === translatedValue
                    );
                    setValue('position', originalKey || positionOptions[0]);
                  }}
                />
              </Box>

              {/* Email (Read-only) */}
              <Box width="100%">
                <Text
                  fontWeight={300}
                  marginBottom="3px"
                  color="alea.500"
                  paddingLeft="16px"
                >
                  {t('emailAddress')}
                </Text>
                <Text
                  fontSize="18px"
                  fontStyle="normal"
                  fontWeight={400}
                  lineHeight="normal"
                  color="#000"
                  paddingLeft="16px"
                >
                  {user?.email}
                </Text>
              </Box>

              {/* Phone Numbers */}
              <Flex width="100%" gap={5}>
                <Box flex="1">
                  <AleaTelephone
                    label={t('telLandline')}
                    value={{
                      code: watch('compound_phone_number')?.code ?? '',
                      phone: watch('compound_phone_number')?.phone ?? '',
                    }}
                    onChange={(value) =>
                      setValue('compound_phone_number', value)
                    }
                    register={register('compound_phone_number')}
                    error={
                      errors.compound_phone_number?.message ||
                      errors.compound_phone_number?.phone?.message
                    }
                  />
                </Box>

                <Box flex="1">
                  <AleaTelephone
                    label={t('telMobile')}
                    value={{
                      code: watch('compound_mobile_phone_number')?.code ?? '',
                      phone: watch('compound_mobile_phone_number')?.phone ?? '',
                    }}
                    onChange={(value) =>
                      setValue('compound_mobile_phone_number', value)
                    }
                    register={register('compound_mobile_phone_number')}
                    error={
                      errors.compound_mobile_phone_number?.message ||
                      errors.compound_mobile_phone_number?.phone?.message
                    }
                  />
                </Box>
              </Flex>
            </VStack>

            <Flex justify="space-between" align="center" mt={8}>
              <Text
                as="button"
                type="button"
                onClick={handleLogout}
                color="orangeAccent.500"
                fontSize="16px"
                fontStyle="normal"
                fontWeight={400}
                textDecoration="underline"
                bg="transparent"
                border="none"
                cursor="pointer"
                p={0}
                lineHeight="normal"
              >
                {t('logOut')}
              </Text>
              <Button
                type="submit"
                colorScheme="alea"
                size="lg"
                w="100px"
                h="50px"
                borderRadius="20px"
                isLoading={isLoading}
              >
                <AleaCheckIcon color="white" size={30} />
              </Button>
            </Flex>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AccountSetupModal;
